import { useEffect } from 'react';
import type { RefObject } from 'react';

interface ScrollRevealOptions {
  mainRef: RefObject<HTMLElement | null>;
  // false (e.g. edit mode) shows everything and removes reveal styling
  enabled: boolean;
}

interface RevealRecord {
  opacity: number | null;
  y: number;
  top?: number;
  bottom?: number;
  stagger?: number;
}

// Port of the template's scroll-reveal.js: scroll-position driven, reversible
// component reveals. The MutationObserver/ResizeObserver on <main> re-measure
// after React re-renders, which makes it safe inside a React tree.
export function useScrollReveal({ mainRef, enabled }: ScrollRevealOptions) {
  useEffect(() => {
    const main = mainRef.current;
    if (!main || !enabled) return;
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    const selector = [
      '.hero-copy > *', '.hero-bottom', '.image-tag', '.section-head',
      '.product-heading > h2', '.product-benefits > li', '.spec', '.material-band',
      '.order-title > *', '.order-flow > h3', '.order-steps > li',
      '.inquiry-panel-header', '.inquiry-fields > div', '.order-note',
      '.contact-grid > .reveal:not(.contact-details) > *',
      '.contact-details > *', 'footer',
      '.catalog-heading > *', '.catalog-group-heading', '.catalog-row', '.catalog-bottom',
    ].join(',');
    const records = new Map<HTMLElement, RevealRecord>();
    let frame = 0;
    let last = 0;
    let dirty = true;
    let height = window.innerHeight;
    let maxScroll = 0;
    let disposed = false;
    const clamp = (x: number) => Math.max(0, Math.min(1, x));
    const ease = (x: number) => {
      x = clamp(x);
      return x * x * (3 - 2 * x);
    };

    function geometry(el: HTMLElement) {
      let top = 0;
      let node: HTMLElement | null = el;
      while (node) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      return { top, bottom: top + el.offsetHeight };
    }

    function measure() {
      height = window.innerHeight;
      maxScroll = Math.max(0, document.documentElement.scrollHeight - height);
      for (const [el] of records) if (!el.isConnected) records.delete(el);
      main!.querySelectorAll<HTMLElement>(selector).forEach(el => {
        if (!records.has(el)) {
          records.set(el, { opacity: null, y: 0 });
          el.classList.add('scroll-component');
        }
        Object.assign(records.get(el)!, geometry(el));
        records.get(el)!.stagger = el.parentElement?.matches('.order-steps')
          ? Array.from(el.parentElement.children).indexOf(el) * 22
          : 0;
      });
      dirty = false;
    }

    function target(r: RevealRecord, y: number) {
      const start = (r.top || 0) - height * 0.98 + (r.stagger || 0);
      const end = Math.min((r.top || 0) - height * 0.72 + (r.stagger || 0), maxScroll);
      const enter = end <= 0 ? 1 : ease((y - start) / Math.max(1, end - start));
      const leave = 1 - ease((y - ((r.bottom || 0) - height * 0.12)) / (height * 0.14));
      return { opacity: Math.min(enter, leave), y: (1 - enter) * 26 - (1 - leave) * 16 };
    }

    function render(time: number) {
      frame = 0;
      if (disposed) return;
      if (dirty) measure();
      const blend = last ? 1 - Math.exp(-Math.min(time - last, 64) / 65) : 1;
      let moving = false;
      for (const [el, r] of records) {
        const t =
          preference.matches || el.contains(document.activeElement)
            ? { opacity: 1, y: 0 }
            : target(r, window.scrollY);
        if (r.opacity === null || preference.matches) {
          r.opacity = t.opacity;
          r.y = t.y;
        } else {
          r.opacity += (t.opacity - r.opacity) * blend;
          r.y += (t.y - r.y) * blend;
        }
        if (Math.abs(t.opacity - r.opacity) < 0.001 && Math.abs(t.y - r.y) < 0.03) {
          r.opacity = t.opacity;
          r.y = t.y;
        } else {
          moving = true;
        }
        el.style.setProperty('--reveal-opacity', r.opacity.toFixed(4));
        el.style.setProperty('--reveal-y', `${r.y.toFixed(2)}px`);
        el.classList.toggle('scroll-component-hidden', r.opacity < 0.05);
      }
      last = time;
      if (moving) frame = requestAnimationFrame(render);
    }

    function schedule() {
      if (!frame && !disposed) frame = requestAnimationFrame(render);
    }

    function refresh() {
      dirty = true;
      schedule();
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('pageshow', refresh);
    window.addEventListener('load', refresh);
    preference.addEventListener('change', refresh);
    main.addEventListener('focusin', schedule);
    main.addEventListener('focusout', schedule);
    document.fonts?.ready.then(() => refresh());
    const mutationObserver = new MutationObserver(refresh);
    mutationObserver.observe(main, { childList: true, subtree: true });
    let resizeObserver: ResizeObserver | undefined;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(refresh);
      resizeObserver.observe(main);
    }
    refresh();

    return () => {
      disposed = true;
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('load', refresh);
      preference.removeEventListener('change', refresh);
      main.removeEventListener('focusin', schedule);
      main.removeEventListener('focusout', schedule);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      // Restore full visibility so edit mode / other routes start clean
      for (const [el] of records) {
        el.classList.remove('scroll-component', 'scroll-component-hidden');
        el.style.removeProperty('--reveal-opacity');
        el.style.removeProperty('--reveal-y');
      }
      records.clear();
    };
  }, [mainRef, enabled]);
}
